/**
 * Keyboard behaviour for this page's hand-rolled radio groups.
 *
 * `PillGroup` and `CardGroup` both render `role="radiogroup"` with `role="radio"` children,
 * and both are right to: these are a single choice from a set, and MUI's `ToggleButtonGroup`
 * announces `aria-pressed` buttons instead, which is a different thing. But taking the ARIA
 * role means taking the ARIA keyboard contract with it, and that half was missing — every
 * option sat in the tab order and the arrow keys did nothing.
 *
 * Two consequences, both real:
 *
 *  - crossing the shape, output and variation groups took eleven Tab presses instead of three;
 *  - a screen reader announces a radio group as one control whose options are reached with
 *    the arrow keys, so a user following that instruction found nothing happened.
 *
 * This restores the standard pattern: roving tabindex — exactly one option in the tab order,
 * the selected one — plus arrow/Home/End navigation that moves selection and focus together,
 * wrapping at both ends.
 *
 * The group's DOM order must match `values`, since focus is moved by querying the container
 * for its radios in document order. That is deliberately not a ref per option: it lets a
 * group render its radios inside whatever wrappers it likes without threading refs through.
 */

import { useRef, type KeyboardEvent } from "react";

export function useRovingRadioGroup<T>(
  values: readonly T[],
  value: T,
  onChange: (next: T) => void,
  disabled = false,
) {
  const containerRef = useRef<HTMLDivElement>(null);

  /*
   * Which option holds the tab stop. Falls back to the first when the current value is not
   * in the list, which happens legitimately here: a series-locked selector displays the
   * series' stored value rather than its own. Without the fallback such a group would have
   * no tab stop at all and be unreachable by keyboard.
   */
  const selectedIndex = Math.max(0, values.indexOf(value));

  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (disabled || values.length === 0) return;

    let nextIndex: number;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (selectedIndex + 1) % values.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = (selectedIndex - 1 + values.length) % values.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = values.length - 1;
        break;
      default:
        return;
    }

    // In range by construction — every branch is modulo `values.length` or a literal end —
    // but `noUncheckedIndexedAccess` cannot see that. Bail rather than assert, so a future
    // branch that *is* out of range does nothing instead of throwing.
    const next = values[nextIndex];
    if (next === undefined) return;

    // Only once the key is known to be one of ours: the arrows must still scroll the page
    // when this group has nothing to move to.
    event.preventDefault();
    onChange(next);

    const radios = containerRef.current?.querySelectorAll<HTMLElement>('[role="radio"]');
    radios?.[nextIndex]?.focus();
  }

  return {
    groupProps: { ref: containerRef, onKeyDown },
    /** Spread onto each radio, in the same order as `values`. */
    getRadioProps: (index: number) => ({ tabIndex: index === selectedIndex ? 0 : -1 }),
  };
}
